import { Import, User } from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";
import { CanExport } from "../misc/CanExport";
import { GlobalSearchButton } from "../search/GlobalSearchButton";
import { ProjectSwitcher } from "../projects/ProjectSwitcher";
import { useCrmModule } from "../misc/fromBitrix";
import { findActiveNavItem, visibleNavItems } from "./navigation";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();
  const activeKey = findActiveNavItem(location.pathname);
  const items = visibleNavItems(useCrmModule());

  return (
    <>
      <div className="grow">
        <header className="bg-background border-b">
          <div className="px-4 md:px-8 py-3">
            <div className="flex justify-between items-center gap-4 flex-1">
              <div className="flex items-center gap-4 min-w-0">
                <Link
                  to="/"
                  className="flex items-center gap-2.5 text-foreground no-underline"
                >
                  <img
                    className="[.light_&]:hidden h-6"
                    src={darkModeLogo}
                    alt={title}
                  />
                  <img
                    className="[.dark_&]:hidden h-6"
                    src={lightModeLogo}
                    alt={title}
                  />
                  <h1 className="text-base font-bold uppercase tracking-[0.18em] text-primary-ink">
                    {title}
                  </h1>
                </Link>
                <ProjectSwitcher />
              </div>
              <div>
                <nav
                  className="flex gap-2"
                  aria-label={translate("crm.navigation.label")}
                >
                  {items.map((item) => {
                    const tab = (
                      <NavigationTab
                        key={item.key}
                        label={translate(item.label, { smart_count: 2 })}
                        to={item.to}
                        isActive={activeKey === item.key}
                      />
                    );
                    return item.access ? (
                      <CanAccess key={item.key} {...item.access}>
                        {tab}
                      </CanAccess>
                    ) : (
                      tab
                    );
                  })}
                </nav>
              </div>
              <div className="flex items-center">
                <GlobalSearchButton />
                <ThemeModeToggle />
                <RefreshButton />
                <UserMenu>
                  <ProfileMenu />
                  <CanExport>
                    <ImportFromJsonMenuItem />
                  </CanExport>
                </UserMenu>
              </div>
            </div>
          </div>
        </header>
      </div>
    </>
  );
};

const NavigationTab = ({
  label,
  to,
  isActive,
}: {
  label: string;
  to: string;
  isActive: boolean;
}) => (
  // raised pills, the current one lit in lemon: the dashboard's menu
  <Link
    to={to}
    className="vv-nav-item"
    aria-current={isActive ? "page" : undefined}
  >
    {label}
  </Link>
);

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem
      onClick={userMenuContext.onClose}
      render={<Link to="/profile" className="flex items-center gap-2" />}
    >
      <User />
      {translate("crm.profile.title")}
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem
      onClick={userMenuContext.onClose}
      render={<Link to={ImportPage.path} className="flex items-center gap-2" />}
    >
      <Import />
      {translate("crm.header.import_data")}
    </DropdownMenuItem>
  );
};

export default Header;
